// Main entry point - UI coordination and game flow

import { ThemeManager } from './theme.js';
import { gameState, loadFactions, getFactions, loadAbilities, getAbilities, Board } from './game.ts';
import type { GameState } from './game.ts';
import { networkManager } from './network.ts';
import type { NetworkManager, NetworkEventHandler } from './network.ts';
import { renderer } from './renderer.ts';
import type { FactionStyleColors } from './renderer.ts';
import { abilityManager } from './abilities.ts';
import type { AbilityManager } from './abilities.ts';
import { abilityRenderer } from './abilityRenderer.ts';
import { safeSetItem } from './utils.ts';
import { shipGraphicsManager } from './shipGraphics.js';
import { SettingsPanel } from './settings-panel.js';
import { BugReport } from './bug-report.ts';
import { playerProfile } from './playerProfile.ts';
import { StandardRemoteDriver } from './drivers/standard.ts';
import type { Coord } from '../../shared/abilityLogic.ts';
import type {
    AbilityResult,
    AbilityTargetPayload,
    CreateAIRoomRequest,
    ErrorMessage,
    RoomCreated,
    StandardDifficulty,
    StandardRoguelikeBattleConfig,
    StandardVariant,
    UpgradedAbility
} from '../../shared/protocol.ts';

// Extracted modules
import * as PlacementPhase from './placementPhase.ts';
import * as BattlePhase from './battlePhase.ts';
import type {
    RoguelikeAIConfig,
    RoguelikeNode as RoguelikeNodeView,
    RoguelikeRunState as RoguelikeRunStateView,
    RunAbilityDef
} from './roguelike.ts';
import * as Reconnection from './reconnection.ts';
import type { StoredSessionExtras } from './reconnection.ts';
import * as NetworkHandlers from './networkHandlers.ts';
import * as ProfileUI from './profileUI.ts';
import type { ProfileUIContext } from './profileUI.ts';
import * as TournamentUI from './tournamentUI.ts';
import type { TournamentUIContext } from './tournamentUI.ts';

// ========================================
// Module/context types
// ========================================

type Renderer = typeof renderer;
type AbilityRenderer = typeof abilityRenderer;
type PlayerProfile = typeof playerProfile;
type RoguelikeModule = typeof import('./roguelike.ts');
type RoguelikeUIModule = typeof import('./roguelikeUI.ts');
type StrategyModule = typeof import('./strategy/main.ts');
type TacticalModule = typeof import('./tactical/main.ts');
type TacticalInitOptions = import('./tactical/main.ts').TacticalInitOptions;
type TacticalShipDefsModule = typeof import('./tactical/shipDefs.ts');

/**
 * The full shared context blob handed to every extracted module's setup().
 * Each converted module declares its own narrow slice (BattlePhaseContext,
 * NetworkHandlersContext, ReconnectionContext, PlacementPhaseContext); this
 * type is the superset that must satisfy all of them — the setup() calls in
 * setupModuleContext() are where the compiler enforces that.
 */
export interface AppContext {
    gameState: GameState;
    renderer: Renderer;
    abilityManager: AbilityManager;
    abilityRenderer: AbilityRenderer;
    networkManager: NetworkManager;
    playerProfile: PlayerProfile;
    getDriver: () => StandardRemoteDriver | null;
    setDriver: (d: StandardRemoteDriver) => void;
    getRoguelike: () => RoguelikeModule;
    getRoguelikeRunState: () => RoguelikeRunStateView | null;
    setRoguelikeRunState: (v: RoguelikeRunStateView | null) => void;
    getRoguelikeCurrentNode: () => RoguelikeNodeView | null;
    setRoguelikeCurrentNode: (v: RoguelikeNodeView | null) => void;
    loadTacticalModule: () => Promise<TacticalModule>;
    getTactical: () => TacticalModule | null;
    handleAbilityClick: (abilityId: string, immediate?: boolean) => void;
    handleUseAbility: (x: number, y: number) => Promise<boolean>;
    handleAbilityResult: (result: AbilityResult) => void;
    initializePlacement: () => void;
    startBattle: () => void;
    endGame: (won: boolean) => void;
    playSound: (name: string) => void;
    clearFireProcessing: () => void;
    incrementTurnCount: () => void;
    updateBlitzCounter: () => void;
    stopRematchCountdown: () => void;
    saveSession: (roomCode: string, sessionToken: string, faction: string | null, extra?: StoredSessionExtras) => void;
    clearSession: () => void;
    setQuickplayMode: (v: boolean) => void;
    initDraftScreen: (mode: string) => void;
    startPickingPhase: (pool: string[]) => void;
    showDifficultySelector: (show: boolean) => void;
    setupPlacementHandlers: () => void;
    selectFaction: (faction: string) => Promise<void>;
}

declare global {
    interface Window {
        /** Exposed for bug report context (bug-report.js). */
        gameState: GameState;
        /** Exposed for external access. */
        playerProfile: PlayerProfile;
        /** Active renderer (standard or tactical) for the graphics-mode
         *  toggle; tactical/main.ts also routes screens/toasts through it
         *  (showToast only exists on the standard renderer). */
        gameRenderer: {
            refreshBoards(): void;
            showScreen(screenId: string): void;
            showToast?(message: string, isError?: boolean, duration?: number): void;
        };
        /** Cross-module hooks read by abilityRenderer's repair targeting UI. */
        confirmRepairTargets?: () => void;
        clearRepairSelection?: () => void;
    }
}

// ========================================
// Lazy-loaded game mode modules
// ========================================
// These are loaded on demand via dynamic import() so esbuild can split them
// into separate chunks. Players who only play standard battles won't download
// tactical (~10K lines), strategy (~5K lines), or roguelike (~2.7K lines) code.

let _tacticalModule: TacticalModule | null = null;
let _tacticalShipDefsModule: TacticalShipDefsModule | null = null;
let _strategyModule: StrategyModule | null = null;
let _roguelikeModule: RoguelikeModule | null = null;
let _roguelikeUIModule: RoguelikeUIModule | null = null;

async function loadTacticalModule(): Promise<TacticalModule> {
    if (!_tacticalModule) {
        _tacticalModule = await import('./tactical/main.ts');
    }
    return _tacticalModule;
}

async function loadTacticalShipDefs(): Promise<TacticalShipDefsModule> {
    if (!_tacticalShipDefsModule) {
        _tacticalShipDefsModule = await import('./tactical/shipDefs.ts');
    }
    return _tacticalShipDefsModule;
}

async function loadStrategyModule(): Promise<StrategyModule> {
    if (!_strategyModule) {
        _strategyModule = await import('./strategy/main.ts');
    }
    return _strategyModule;
}

async function loadRoguelikeModule(): Promise<RoguelikeModule> {
    if (!_roguelikeModule) {
        _roguelikeModule = await import('./roguelike.ts');
    }
    return _roguelikeModule;
}

async function loadRoguelikeUIModule(): Promise<RoguelikeUIModule> {
    if (!_roguelikeUIModule) {
        _roguelikeUIModule = await import('./roguelikeUI.ts');
    }
    return _roguelikeUIModule;
}

// Convenience: get already-loaded module synchronously. These are safe to
// call from event handlers since all modules are loaded during init()
// (getTactical alone stays nullable — tactical truly loads on demand).
function getRoguelike(): RoguelikeModule { return _roguelikeModule!; }
function getRoguelikeUI(): RoguelikeUIModule { return _roguelikeUIModule!; }
function getStrategy(): StrategyModule { return _strategyModule!; }
function getTactical(): TacticalModule | null { return _tacticalModule; }

// Expose gameState for bug report context
window.gameState = gameState;
// Expose playerProfile for external access
window.playerProfile = playerProfile;

// Quickplay state
let isQuickplayMode = false;
let quickplayName = '';

// Roguelike campaign state
let roguelikeRunState: RoguelikeRunStateView | null = null;
let roguelikeCurrentNode: RoguelikeNodeView | null = null;

// Multi-cell repair targeting state
let repairTargets: Coord[] = [];   // Array of {x, y} selected so far
let repairMaxCells = 1;            // cellsPerUse for current repair activation

// Standard mode driver (StandardRemoteDriver — MP rooms and SP AI rooms alike)
let driver: StandardRemoteDriver | null = null;

// Shared context object for extracted modules
function buildContext(): AppContext {
    return {
        gameState,
        renderer,
        abilityManager,
        abilityRenderer,
        networkManager,
        playerProfile,
        getDriver: () => driver,
        setDriver: (d) => { driver = d; },
        getRoguelike,
        getRoguelikeRunState: () => roguelikeRunState,
        setRoguelikeRunState: (v) => { roguelikeRunState = v; },
        getRoguelikeCurrentNode: () => roguelikeCurrentNode,
        setRoguelikeCurrentNode: (v) => { roguelikeCurrentNode = v; },
        loadTacticalModule,
        getTactical,
        handleAbilityClick,
        handleUseAbility,
        handleAbilityResult: BattlePhase.handleAbilityResult,
        initializePlacement: PlacementPhase.initializePlacement,
        startBattle: BattlePhase.startBattle,
        endGame: BattlePhase.endGame,
        playSound: BattlePhase.playSound,
        clearFireProcessing: BattlePhase.clearFireProcessing,
        incrementTurnCount: BattlePhase.incrementTurnCount,
        updateBlitzCounter: BattlePhase.updateBlitzCounter,
        stopRematchCountdown: BattlePhase.stopRematchCountdown,
        saveSession: Reconnection.saveSession,
        clearSession: Reconnection.clearSession,
        setQuickplayMode: (v) => { isQuickplayMode = v; },
        initDraftScreen: TournamentUI.initDraftScreen,
        startPickingPhase: TournamentUI.startPickingPhase,
        showDifficultySelector,
        setupPlacementHandlers,
        selectFaction,
    };
}

// Set up all extracted modules with shared context. The typed setup() calls
// (and the satisfies checks for the still-JS modules) are what guarantee the
// blob covers every consumer's declared context interface.
function setupModuleContext(): void {
    const context = buildContext();
    PlacementPhase.setup(context);
    BattlePhase.setup(context);
    Reconnection.setup(context);
    NetworkHandlers.setup(context);
    ProfileUI.setup(context satisfies ProfileUIContext);
    TournamentUI.setup(context satisfies TournamentUIContext);
    // RoguelikeUI is lazy-loaded but pre-loaded in init()
    getRoguelikeUI().setup(context);
}

// ========================================
// Startup loading state
// ========================================
// The menu's mode buttons ship `disabled data-boot-lock` in index.html so a
// click during startup can't land on a button whose handler isn't wired yet
// (the handlers need the module context, which needs the data loads in
// init()). init() unlocks them and sets document.body.dataset.ready = '1' —
// the deterministic signal the E2E helpers wait on — in the same synchronous
// block that wires the handlers.

function setMenuReady(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-boot-lock]').forEach(btn => {
        btn.disabled = false;
        btn.removeAttribute('data-boot-lock');
    });
    document.getElementById('menu-loading')?.classList.add('hidden');
    document.body.dataset.ready = '1';
}

function showStartupError(): void {
    const loading = document.getElementById('menu-loading');
    if (loading) {
        loading.classList.add('error');
        loading.textContent = 'Failed to load. Please refresh the page.';
    }
}

// Ship SVGs only matter once a board with ships is on screen (placement or
// battle), so they load in the background instead of blocking the menu.
// The renderers already fall back to plain cells when an SVG isn't cached;
// if a standard board was drawn before the preload landed, re-render it (the
// same path the graphics-mode toggle uses). Tactical sessions join this
// preload in initTactical() and refresh their own TacticalRenderer — during
// tactical, window.gameRenderer is still this standard renderer.
function preloadShipGraphicsInBackground(): void {
    shipGraphicsManager.preloadSVGs()
        .then(() => {
            if (gameState.playerBoard && !window.tacticalActive) {
                renderer.refreshBoards();
            }
        })
        .catch((err: unknown) => console.warn('[ShipGraphics] Background preload failed:', err));
}

// Initialize the application
async function init(): Promise<void> {
    preloadShipGraphicsInBackground();
    // An open socket must pick up a newly bound profile session (no-op if not connected)
    playerProfile.onSessionRebound = () => networkManager.refreshSession();

    // Every startup fetch/chunk below is independent (each fills its own
    // module state), so they run in parallel. The config loaders already fall
    // back to defaults on failure; the .catch()es keep it that way.
    const [, , , Roguelike, Strategy] = await Promise.all([
        // Player profile (creates anonymous if needed)
        playerProfile.init(),
        // Factions and abilities (faction picker, ability UI)
        loadFactions(),
        loadAbilities(),
        // Game mode configs decide which menu buttons to show
        loadRoguelikeModule().then(async mod => {
            await mod.loadConfig().catch(err => console.warn('Roguelike config load failed, using defaults:', err));
            return mod;
        }),
        loadStrategyModule().then(async mod => {
            await mod.loadConfig().catch(err => console.warn('Strategy config load failed, using defaults:', err));
            return mod;
        }),
        // Tactical ship class and ability definitions (needed by fleet builder UI)
        loadTacticalShipDefs().then(shipDefs => Promise.all([
            shipDefs.loadShipClassesFromServer().catch(err => console.warn('Ship class load failed, using defaults:', err)),
            shipDefs.loadTacticalAbilitiesFromServer().catch(err => console.warn('Tactical ability load failed, using defaults:', err)),
        ])),
        // Pre-load RoguelikeUI module (setup deferred to setupModuleContext)
        loadRoguelikeUIModule(),
    ]);

    // Everything from here through setMenuReady() is synchronous, so the
    // menu's visibility tweaks, handler wiring and unlock land in one task.

    // Wire tactical bridge lazily — Strategy will call these when needed
    Strategy.setTacticalBridge({
        initTactical: async (opts?: TacticalInitOptions) => {
            const mod = await loadTacticalModule();
            return mod.initTactical(opts);
        },
        cleanupTactical: () => {
            // cleanupTactical is sync and only called after tactical is loaded
            if (_tacticalModule) _tacticalModule.cleanupTactical();
        },
    });

    // Hide roguelike buttons if disabled by admin
    const roguelikeBtn = document.getElementById('btn-roguelike');
    if (roguelikeBtn && !Roguelike.isEnabled()) {
        roguelikeBtn.style.display = 'none';
    }
    const tacticalRoguelikeBtn = document.getElementById('btn-tactical-roguelike');
    if (tacticalRoguelikeBtn && !Roguelike.isTacticalEnabled()) {
        tacticalRoguelikeBtn.style.display = 'none';
    }

    // Hide entire strategy menu group if disabled by admin
    const strategyGroup = document.querySelector<HTMLElement>('.menu-group-strategy');
    if (strategyGroup && !Strategy.isEnabled()) {
        strategyGroup.style.display = 'none';
    }

    // Show strategy resume banner if saved game exists and strategy is enabled
    if (Strategy.isEnabled() && Strategy.hasSavedGame()) {
        const banner = document.getElementById('strategy-resume-banner');
        const details = document.getElementById('strategy-resume-details');
        if (banner) {
            banner.classList.remove('hidden');
            const info = Strategy.getSavedGameInfo();
            if (details && info) {
                details.textContent = `Turn ${info.turn} · ${info.systemsClaimed} systems`;
            }
        }
    }

    renderer.initializeBoards();
    window.gameRenderer = renderer; // Expose for settings toggle

    // Set up extracted module context (must be after data loading)
    setupModuleContext();

    setupMenuHandlers();
    setupLobbyHandlers();
    setupFactionHandlers();
    setupPlacementHandlers();
    setupBattleHandlers();
    setupGameOverHandlers();
    setupTacticalGameOverHandlers();
    NetworkHandlers.setupNetworkHandlers();
    Reconnection.setupReconnectionHandlers();
    setupAbilityHandlers();
    setupStrategyHandlers();
    getRoguelikeUI().setupRoguelikeHandlers();
    SettingsPanel.init();
    BugReport.init();

    // Highlight "WHAT'S NEW" for the first 3 visits after each release. The
    // key names the newest release in release-notes.html (bump both together;
    // tests/whatsNew.test.js checks they match).
    const whatsNewBtn = document.getElementById('btn-whats-new');
    if (whatsNewBtn) {
        const VISIT_KEY = 'djb_release_visits_x5';
        let visits = parseInt(localStorage.getItem(VISIT_KEY)!) || 0;
        visits++;
        safeSetItem(VISIT_KEY, String(visits));
        if (visits <= 3) {
            whatsNewBtn.classList.add('whats-new-highlight');
        }
    }

    // Render dynamic faction selection
    renderFactionSelection();

    // Auto-fill multiplayer name fields with callsign if registered
    if (playerProfile.callsign) {
        const nameInputs = ['quickplay-name', 'create-name', 'join-name'];
        for (const id of nameInputs) {
            const el = document.getElementById(id) as HTMLInputElement | null;
            if (el && !el.value) el.value = playerProfile.callsign;
        }
    }

    // Handlers are wired and the faction picker is rendered: unlock the menu.
    setMenuReady();

    // Check for existing session that can be rejoined
    const session = Reconnection.getSession();
    const urlParams = new URLSearchParams(window.location.search);
    const tournamentCode = urlParams.get('tournamentCode');
    const roomCode = urlParams.get('roomCode');
    const urlPlayerName = urlParams.get('playerName');

    if (tournamentCode && roomCode) {
        // Tournament match join
        gameState.mode = 'multiplayer';
        gameState.roomCode = roomCode;
        gameState.tournamentCode = tournamentCode;
        gameState.tournamentGameMode = urlParams.get('gameMode') || 'standard';
        gameState.tournamentFactionDraft = urlParams.get('factionDraft') === '1';

        if (urlPlayerName) {
            gameState.playerName = urlPlayerName.replace(/[<>"'&]/g, '').substring(0, 15);
        } else if (!gameState.playerName) {
            // Check session or fallback. (StoredSession has never persisted a
            // playerName, so this legacy lookup always falls through to
            // 'Player' — kept verbatim; flagged in the cleanup backlog.)
            const savedSession = Reconnection.getSession() as (Reconnection.StoredSession & { playerName?: string }) | null;
            if (savedSession && savedSession.playerName) {
                gameState.playerName = savedSession.playerName;
            } else {
                 gameState.playerName = 'Player';
            }
        }

        // Set tactical flag based on tournament game mode
        if (gameState.tournamentGameMode === 'tactical') {
            gameState.isTactical = true;
        }

        if (gameState.tournamentFactionDraft) {
            // Faction draft: skip faction selection, connect immediately with placeholder
            try {
                await networkManager.connect();
                const existingSession = Reconnection.getSession();
                // Use first available active faction as placeholder — overwritten after draft
                const allFactions = getFactions();
                const placeholderFaction = Object.keys(allFactions).find(id => {
                    const f = allFactions[id];
                    return f.active !== false && !f.roguelikeOnly;
                }) || 'taldryan';
                networkManager.joinTournamentRoom(roomCode, tournamentCode, placeholderFaction, existingSession?.sessionToken);
            } catch {
                renderer.showToast('Failed to connect to server', true);
            }
            renderer.showScreen('draft-screen');
            TournamentUI.initDraftScreen('waiting');
            BugReport.setCurrentScreen('draft');
        } else {
            showDifficultySelector(false);
            renderer.showScreen('faction-screen');
            BugReport.setCurrentScreen('faction');
        }
    } else if (session) {
        await Reconnection.checkAndOfferReconnection(session);
    } else {
        // Show menu screen
        renderer.showScreen('menu-screen');
        BugReport.setCurrentScreen('menu');
    }
}

// Set up ability-related handlers
function setupAbilityHandlers(): void {
    // Escape key to cancel ability targeting
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && abilityManager.isTargeting()) {
            clearRepairSelection();
            abilityManager.cancelTargeting();
            abilityRenderer.hideTargetingMode();
            abilityRenderer.renderAbilities(gameState.isMyTurn);
        }
    });

    // Listen for ability state changes
    abilityManager.on('updated', () => {
        abilityRenderer.updateBuffs();
    });
}

// Render faction selection cards dynamically (with HTML escaping for security)
function renderFactionSelection(): void {
    const container = document.querySelector('.faction-container')!;
    const factions = getFactions();
    const abilities = getAbilities();

    container.innerHTML = '';

    for (const [id, faction] of Object.entries(factions)) {
        // Skip roguelike-only factions (e.g. Raiders) from the player faction picker
        if (faction.roguelikeOnly) continue;

        const card = document.createElement('div');
        card.className = 'faction-card';
        card.dataset.faction = id;

        // Apply faction colors (validated hex colors only)
        const colors: FactionStyleColors = faction.colors || { primary: '#00aaff', secondary: '#0088cc' };
        const primaryColor = /^#[0-9A-Fa-f]{6}$/.test(colors.primary) ? colors.primary : '#00aaff';
        const secondaryColor = /^#[0-9A-Fa-f]{6}$/.test(colors.secondary) ? colors.secondary : '#0088cc';
        const glowColor = colors.glow && /^(#[0-9A-Fa-f]{6,8}|rgba?\([0-9,.\s]+\))$/.test(colors.glow) ? colors.glow : `${primaryColor}99`;

        card.style.setProperty('--card-primary', primaryColor);
        card.style.setProperty('--card-secondary', secondaryColor);
        card.style.setProperty('--card-glow', glowColor);

        // Create emblem div
        const emblemDiv = document.createElement('div');
        emblemDiv.className = 'faction-emblem';
        // Use clan logo from /images/logos/ or generate simple emblem
        const emblemUrl = `/djbship/images/logos/${encodeURIComponent(id)}.svg?v=5`;
        emblemDiv.style.backgroundImage = `url('${emblemUrl}')`;

        // Create title
        const titleEl = document.createElement('h3');
        titleEl.style.color = primaryColor;
        titleEl.textContent = faction.name.toUpperCase();

        // Create abilities container - sort active abilities first
        const abilitiesDiv = document.createElement('div');
        abilitiesDiv.className = 'faction-abilities';
        const factionAbilities = (faction.abilities || [])
            .map(abilityId => abilities[abilityId])
            .filter(a => a)
            .sort((a, b) => {
                // Active abilities first, then passive
                if (a.type === 'active' && b.type === 'passive') return -1;
                if (a.type === 'passive' && b.type === 'active') return 1;
                return 0;
            });
        factionAbilities.forEach(ability => {
            const abilityDiv = document.createElement('div');
            abilityDiv.className = `faction-ability ${ability.type}`;
            const typeSpan = document.createElement('span');
            typeSpan.className = 'ability-type';
            typeSpan.textContent = ability.type === 'passive' ? 'Passive:' : 'Active:';
            const nameSpan = document.createElement('span');
            nameSpan.className = 'ability-name';
            nameSpan.textContent = ability.name;
            abilityDiv.appendChild(typeSpan);
            abilityDiv.appendChild(document.createTextNode(' '));
            abilityDiv.appendChild(nameSpan);
            abilitiesDiv.appendChild(abilityDiv);
        });

        // Create ships container
        const shipsDiv = document.createElement('div');
        shipsDiv.className = 'faction-ships';
        faction.ships.forEach(ship => {
            const shipSpan = document.createElement('span');
            shipSpan.textContent = ship.name;
            shipsDiv.appendChild(shipSpan);
        });

        // Assemble card
        card.appendChild(emblemDiv);
        card.appendChild(titleEl);
        card.appendChild(abilitiesDiv);
        card.appendChild(shipsDiv);

        // Hover/selected highlighting is pure CSS (menus.css) driven by the
        // --card-primary/--card-glow variables above — no inline styles that
        // could outlive the hover or the selection
        card.addEventListener('click', () => selectFaction(id));

        container.appendChild(card);
    }
}

// Menu screen handlers
function setupMenuHandlers(): void {
    document.getElementById('btn-standard-sp')!.addEventListener('click', () => {
        gameState.mode = 'singleplayer';
        gameState.isBlitz = false;
        gameState.isTimeTrial = false;
        gameState.isTactical = false;
        showDifficultySelector(true);
        renderer.showScreen('faction-screen');
        BugReport.setCurrentScreen('faction');
    });

    document.getElementById('btn-tactical-sp')!.addEventListener('click', () => {
        gameState.mode = 'singleplayer';
        gameState.isTactical = true;
        gameState.isBlitz = false;
        gameState.isTimeTrial = false;
        showDifficultySelector(false);
        renderer.showScreen('faction-screen');
        BugReport.setCurrentScreen('faction');
    });


    document.getElementById('btn-roguelike')!.addEventListener('click', () => {
        if (roguelikeRunState && roguelikeRunState.runActive) {
            getRoguelikeUI().highlightResumeBanner();
            return;
        }
        gameState.mode = 'singleplayer';
        gameState.isRoguelike = true;
        gameState.isBlitz = false;
        gameState.isTimeTrial = false;
        gameState.isTactical = false;
        gameState.isTacticalRoguelike = false;
        showDifficultySelector(false);
        renderer.showScreen('faction-screen');
        BugReport.setCurrentScreen('faction');
    });

    document.getElementById('btn-tactical-roguelike')?.addEventListener('click', () => {
        if (!getRoguelike().isTacticalEnabled()) return;
        if (roguelikeRunState && roguelikeRunState.runActive) {
            getRoguelikeUI().highlightResumeBanner();
            return;
        }
        gameState.mode = 'singleplayer';
        gameState.isRoguelike = true;
        gameState.isTactical = true;
        gameState.isTacticalRoguelike = true;
        gameState.isBlitz = false;
        gameState.isTimeTrial = false;
        showDifficultySelector(false);
        renderer.showScreen('faction-screen');
        BugReport.setCurrentScreen('faction');
    });

    document.getElementById('btn-standard-mp')!.addEventListener('click', () => {
        gameState.mode = 'multiplayer';
        gameState.isTactical = false;
        renderer.showScreen('lobby-screen');
        BugReport.setCurrentScreen('lobby');
    });

    document.getElementById('btn-tactical-mp')!.addEventListener('click', () => {
        gameState.mode = 'multiplayer';
        gameState.isTactical = true;
        renderer.showScreen('lobby-screen');
        BugReport.setCurrentScreen('lobby');
    });

    // Player profile UI
    ProfileUI.setupPlayerProfileHandlers();
    ProfileUI.updatePlayerProfileBar();

    // Difficulty selector
    setupDifficultyHandlers();

    // Tournament banner handlers
    TournamentUI.setupTournamentBanner();

    // Faction draft handlers
    TournamentUI.setupDraftSocketHandlers();
    TournamentUI.setupDraftSubmitHandler();
}

// Player profile UI functions moved to profileUI.js

// Difficulty selector — show/hide and sync with player profile
function showDifficultySelector(show: boolean): void {
    const selector = document.getElementById('difficulty-selector');
    if (!selector) return;

    if (show) {
        selector.classList.remove('hidden');
        // Sync active button with player's saved difficulty
        const current = playerProfile.difficulty || 'knight';
        selector.querySelectorAll<HTMLElement>('.difficulty-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.difficulty === current);
        });
        // Show blitz toggle for standard SP. The checkboxes mirror the current
        // gameState variant (callers set it first: the menu clears it, SP
        // REMATCH keeps it) — never reset it here, or a Blitz rematch would
        // silently become a normal game.
        const blitzToggle = document.getElementById('blitz-toggle');
        const blitzCheckbox = document.getElementById('blitz-checkbox') as HTMLInputElement | null;
        if (blitzToggle) {
            blitzToggle.classList.remove('hidden');
            if (blitzCheckbox) blitzCheckbox.checked = gameState.isBlitz;
        }

        // Show time trial toggle (hidden when blitz is active — mutually exclusive)
        const ttToggle = document.getElementById('time-trial-toggle');
        const ttCheckbox = document.getElementById('time-trial-checkbox') as HTMLInputElement | null;
        if (ttToggle) {
            ttToggle.classList.toggle('hidden', gameState.isBlitz);
            if (ttCheckbox) ttCheckbox.checked = gameState.isTimeTrial && !gameState.isBlitz;
        }
    } else {
        selector.classList.add('hidden');
    }
}

function setupDifficultyHandlers(): void {
    const selector = document.getElementById('difficulty-selector');
    if (!selector) return;

    selector.querySelectorAll<HTMLElement>('.difficulty-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
            const diff = btn.dataset.difficulty;

            // Update UI
            selector.querySelectorAll('.difficulty-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            // Save to server + local state
            await playerProfile.setDifficulty(diff);

            // Update time trial best display if toggle is on
            if (gameState.isTimeTrial) updateTimeTrialBest();
        });
    });

    // Blitz toggle
    const blitzCheckbox = document.getElementById('blitz-checkbox') as HTMLInputElement | null;
    if (blitzCheckbox) {
        blitzCheckbox.addEventListener('change', () => {
            gameState.isBlitz = blitzCheckbox.checked;
            // When blitz is on, hide time trial (they're mutually exclusive)
            const ttToggle = document.getElementById('time-trial-toggle');
            const ttCheckbox = document.getElementById('time-trial-checkbox') as HTMLInputElement | null;
            if (blitzCheckbox.checked) {
                if (ttToggle) ttToggle.classList.add('hidden');
                if (ttCheckbox) {
                    ttCheckbox.checked = false;
                    gameState.isTimeTrial = false;
                }
            } else {
                if (ttToggle) ttToggle.classList.remove('hidden');
            }
        });
    }

    // Time trial toggle
    const ttCheckbox = document.getElementById('time-trial-checkbox') as HTMLInputElement | null;
    if (ttCheckbox) {
        ttCheckbox.addEventListener('change', () => {
            gameState.isTimeTrial = ttCheckbox.checked;
            if (gameState.isTimeTrial) {
                updateTimeTrialBest();
            } else {
                const bestEl = document.getElementById('time-trial-best');
                if (bestEl) bestEl.classList.add('hidden');
            }
        });
    }
}

async function updateTimeTrialBest(): Promise<void> {
    const bestEl = document.getElementById('time-trial-best');
    if (!bestEl || !playerProfile.id) {
        if (bestEl) bestEl.classList.add('hidden');
        return;
    }

    try {
        const resp = await fetch(`/djbship/api/player/${playerProfile.id}`);
        if (!resp.ok) throw new Error();
        const data = await resp.json();
        const difficulty = playerProfile.difficulty || 'knight';
        const ttStat = data.stats?.find((s: { mode: string; difficulty: string; bestTime: number }) =>
            s.mode === 'time-trial' && s.difficulty === difficulty);

        if (ttStat && ttStat.bestTime > 0) {
            const mins = Math.floor(ttStat.bestTime / 60);
            const secs = ttStat.bestTime % 60;
            bestEl.textContent = `Best: ${mins}:${secs.toString().padStart(2, '0')}`;
            bestEl.classList.remove('hidden');
        } else {
            bestEl.textContent = 'No best time yet';
            bestEl.classList.remove('hidden');
        }
    } catch {
        bestEl.classList.add('hidden');
    }
}

// Tournament banner, registration, code entry, and faction draft functions moved to tournamentUI.js



// Lobby screen handlers (multiplayer)
function setupLobbyHandlers(): void {
    document.getElementById('btn-create-room')!.addEventListener('click', async () => {
        const name = (document.getElementById('create-name') as HTMLInputElement).value.trim();
        if (!name) {
            renderer.showToast('Please enter your name', true);
            return;
        }

        gameState.playerName = name;
        await connectAndCreateRoom();
    });

    document.getElementById('btn-join-room')!.addEventListener('click', async () => {
        const name = (document.getElementById('join-name') as HTMLInputElement).value.trim();
        const code = (document.getElementById('join-code') as HTMLInputElement).value.trim().toUpperCase();

        if (!name) {
            renderer.showToast('Please enter your name', true);
            return;
        }
        if (!code || code.length !== 6) {
            renderer.showToast('Please enter a valid 6-letter room code', true);
            return;
        }

        gameState.playerName = name;
        gameState.roomCode = code;

        // Show faction selection before joining
        showDifficultySelector(false);
        renderer.showScreen('faction-screen');
    });

    document.getElementById('btn-back-menu')!.addEventListener('click', () => {
        renderer.showScreen('menu-screen');
        BugReport.setCurrentScreen('menu');
    });

    document.getElementById('btn-cancel-wait')!.addEventListener('click', () => {
        if (gameState.roomCode) {
            networkManager.leaveRoom(gameState.roomCode);
        }
        gameState.reset();
        renderer.showScreen('lobby-screen');
        BugReport.setCurrentScreen('lobby');
    });

    // Quickplay button
    const btnQuickplay = document.getElementById('btn-quickplay');
    if (btnQuickplay) {
        btnQuickplay.addEventListener('click', async () => {
            const name = (document.getElementById('quickplay-name') as HTMLInputElement).value.trim();
            if (!name) {
                renderer.showToast('Please enter your name', true);
                return;
            }
            quickplayName = name;
            gameState.playerName = name;
            gameState.mode = 'multiplayer';
            isQuickplayMode = true;
            showDifficultySelector(false);
            renderer.showScreen('faction-screen');
        });
    }

    // Cancel queue button
    const btnCancelQueue = document.getElementById('btn-cancel-queue');
    if (btnCancelQueue) {
        btnCancelQueue.addEventListener('click', () => {
            networkManager.cancelQuickplay();
            isQuickplayMode = false;
            renderer.showScreen('lobby-screen');
        });
    }
}

// Connect to server and create room
async function connectAndCreateRoom(): Promise<void> {
    try {
        await networkManager.connect();
        // Show faction selection - room will be created after faction is chosen
        showDifficultySelector(false);
        renderer.showScreen('faction-screen');
    } catch {
        renderer.showToast('Failed to connect to server', true);
    }
}

// Faction selection handlers
function setupFactionHandlers(): void {
    // Faction card click handlers are set up in renderFactionSelection()

    document.getElementById('btn-back-lobby')!.addEventListener('click', () => {
        if (gameState.mode === 'singleplayer') {
            // Abort an in-flight server-SP room creation so a late
            // room-created event can't yank the player back to placement
            abandonPendingStandardAIRoom();
            gameState.isRoguelike = false;
            gameState.isTactical = false;
            renderer.showScreen('menu-screen');
        } else {
            renderer.showScreen('lobby-screen');
        }
    });
}

// Handle faction selection
async function selectFaction(faction: string): Promise<void> {
    const factions = getFactions();
    const factionData = factions[faction];

    // In roguelike, exclude fully destroyed ships from placement
    const destroyedIds = (gameState.isRoguelike && roguelikeRunState)
        ? getRoguelike().getDestroyedShipIds(roguelikeRunState) : [];
    gameState.initializeShips(faction, destroyedIds);

    // If all ships destroyed, end the run
    if (gameState.isRoguelike && roguelikeRunState && gameState.ships.length === 0) {
        roguelikeRunState.runActive = false;
        getRoguelikeUI().postCampaignStats(roguelikeRunState);
        getRoguelike().saveActiveRun(roguelikeRunState);
        getRoguelike().saveRunToHistory(roguelikeRunState);
        getRoguelike().clearActiveRun();
        renderer.showToast('All ships destroyed — roguelike over.');
        setTimeout(() => {
            renderer.showScreen('menu-screen');
        }, 1500);
        return;
    }

    renderer.setFactionStyle(faction, factionData.colors);
    renderer.setFactionColors(factionData.colors);

    // Set faction in theme manager for CSS variables
    ThemeManager.setFaction(faction);

    // Initialize abilities for this faction
    if (factionData.abilityData) {
        abilityManager.initialize(factionData.abilityData);
    } else {
        abilityManager.reset();
    }

    // Highlight selected faction card
    document.querySelectorAll<HTMLElement>('.faction-card').forEach(card => {
        card.classList.toggle('selected', card.dataset.faction === faction);
    });

    if (gameState.mode === 'singleplayer') {
        // Set player name from profile
        gameState.playerName = playerProfile.displayName;

        // Strategy mode: launch galactic campaign
        if (gameState.isStrategy) {
            gameState.isStrategy = false; // Reset flag
            await getStrategy().initStrategy({
                faction: faction,
                difficulty: playerProfile.difficulty || 'knight',
                renderer: renderer,
            });
            return;
        }

        // Tactical Roguelike: create run and show map
        if (gameState.isTacticalRoguelike && !roguelikeRunState) {
            roguelikeRunState = getRoguelike().createRunState(faction, 'tactical');
            getRoguelike().saveActiveRun(roguelikeRunState);
            getRoguelikeUI().renderRoguelikeMap();
            renderer.showScreen('roguelike-map-screen');
            BugReport.setCurrentScreen('roguelike-map');
            return;
        }

        // Tactical Roguelike: continuing battle (have existing run state)
        if (gameState.isTacticalRoguelike && roguelikeRunState && roguelikeCurrentNode) {
            getRoguelikeUI().startTacticalRoguelikeBattle(faction);
            return;
        }

        // Tactical SP: launch tactical mode (non-campaign)
        if (gameState.isTactical && !gameState.isTacticalRoguelike) {
            const tactical = await loadTacticalModule();
            await tactical.initTactical({
                faction: faction,
                mode: 'singleplayer',
                socket: null,
                mainRenderer: renderer,
            });
            return;
        }

        // Standard Roguelike campaign: create run and show map instead of placement
        if (gameState.isRoguelike && !roguelikeRunState) {
            roguelikeRunState = getRoguelike().createRunState(faction);
            getRoguelike().saveActiveRun(roguelikeRunState);
            getRoguelikeUI().renderRoguelikeMap();
            renderer.showScreen('roguelike-map-screen');
            BugReport.setCurrentScreen('roguelike-map');
            return;
        }

        // Standard battles (plain SP incl. blitz/time-trial AND roguelike
        // campaign nodes). The opponent display name and the (roguelike)
        // resolved AI config are shared by both the server-hosted and
        // fully-local paths.
        const aiConfig: RoguelikeAIConfig | null = (gameState.isRoguelike && roguelikeCurrentNode)
            ? getRoguelike().buildAIConfig(roguelikeCurrentNode)
            : null;

        if (aiConfig) {
            const nodeLabel = roguelikeCurrentNode!.type === 'boss' ? 'BOSS' :
                roguelikeCurrentNode!.type === 'elite' ? 'ELITE' : 'AI';
            const diffNames: Record<string, string> = { apprentice: 'Apprentice', knight: 'Commander', master: 'Master' };
            gameState.opponentName = `${nodeLabel} ${diffNames[aiConfig.difficulty] || 'Commander'}`;

            // Initialize player abilities from run state (may have extra abilities from rewards)
            if (roguelikeRunState!.abilities) {
                abilityManager.initialize(roguelikeRunState!.abilities);
            }
        } else if (gameState.isBlitz) {
            gameState.opponentName = 'AI Fleet';
        } else {
            const diffNames: Record<string, string> = { apprentice: 'AI Apprentice', knight: 'AI Commander', master: 'AI Master' };
            gameState.opponentName = diffNames[playerProfile.difficulty] || 'AI Commander';
        }

        // Server-hosted SP via a standard AI room — the existing MP client
        // flow (StandardRemoteDriver + networkHandlers listeners) drives the
        // battle from placement onwards.
        await startStandardServerSP(faction, aiConfig);
    } else {
        // Multiplayer - create, join, or quickplay
        try {
            await networkManager.connect();

            if (isQuickplayMode) {
                // Quickplay - join matchmaking queue
                const factions = getFactions();
                const factionData = factions[faction];
                const factionAbilities = factionData?.abilities || [];
                const session = Reconnection.getSession();
                const gameType = gameState.isTactical ? 'tactical' : 'standard';
                networkManager.joinQuickplay(quickplayName, faction, factionAbilities, session?.sessionToken, gameType);
                renderer.showScreen('queue-screen');
                BugReport.setCurrentScreen('queue');
            } else if (!gameState.roomCode) {
                // Creating room
                if (gameState.isTactical) {
                    networkManager.send('create-room', {
                        playerName: gameState.playerName,
                        faction,
                        gameType: 'tactical'
                    });
                } else {
                    networkManager.createRoom(gameState.playerName, faction);
                }
            } else {
                // Joining room
                if (gameState.tournamentCode) {
                    // Include session token for secure reconnection
                    const session = Reconnection.getSession();
                    networkManager.joinTournamentRoom(gameState.roomCode, gameState.tournamentCode, faction, session?.sessionToken);
                } else {
                    networkManager.joinRoom(gameState.roomCode, gameState.playerName, faction);
                }
            }
        } catch {
            renderer.showToast('Failed to connect to server', true);
        }
    }
}

// ========================================
// Server-hosted standard single-player (AI rooms)
// ========================================
// Standard SP — plain games (incl. blitz/time-trial) AND roguelike campaign
// battles — plays against a server-side AI room and reuses the whole MP
// client flow: StandardRemoteDriver, the networkHandlers listeners
// (shot-result / opponent-shot / turn-change / ability events) and the normal
// ships-placed → game-start pipeline. Roguelike config crosses the wire on
// room creation as resolved data; the server applies pre-battle damage and
// reports it via game-start.roguelike.appliedDamage. There is no local
// engine: if the server is unreachable, SP cannot start — the player gets an
// error toast and returns to the menu.

let standardAiRoomHandler: NetworkEventHandler | null = null;       // one-shot room-created listener (cleanup)
let standardAiRoomErrorHandler: NetworkEventHandler | null = null;  // one-shot create-ai-room-error listener

let standardAiRoomGeneration = 0;  // bumped by abandonPendingStandardAIRoom (cancels a start still connecting)
// Correlation id of the creation request in flight: the server echoes it on
// room-created / create-ai-room-error, so a reply to an abandoned request is
// never adopted by a newer one, however late it arrives.
let standardAiRequestId: string | null = null;
// Generation of the start that is between its guard and registering its
// listener (null: none). Backing out bumps the generation, which releases it.
let standardAiStartInFlight: number | null = null;
const ORPHAN_LISTENER_MS = 60_000;   // only frees the orphan listener; correctness comes from the id

function newAiRequestId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function cancelPendingStandardAIRoom(): void {
    if (standardAiRoomHandler) {
        networkManager.off('room-created', standardAiRoomHandler);
        standardAiRoomHandler = null;
    }
    if (standardAiRoomErrorHandler) {
        networkManager.off('create-ai-room-error', standardAiRoomErrorHandler);
        standardAiRoomErrorHandler = null;
    }
}

/**
 * The player left (MAIN MENU after a REMATCH, Back on the faction screen)
 * while a server-SP room was still being created: the start becomes a no-op.
 * A start still connecting stops at its generation check; one waiting on
 * room-created loses its listener, and the room the server creates anyway is
 * left as soon as it arrives so it doesn't linger until the room timeout.
 */
function abandonPendingStandardAIRoom(): void {
    standardAiRoomGeneration++;
    if (!standardAiRoomHandler) return;
    cancelPendingStandardAIRoom();

    const orphanId = standardAiRequestId;
    standardAiRequestId = null;
    if (!orphanId) return;
    const dropOrphan = (): void => {
        clearTimeout(expire);
        networkManager.off('room-created', onOrphanCreated);
        networkManager.off('create-ai-room-error', onOrphanError);
    };
    const expire = setTimeout(dropOrphan, ORPHAN_LISTENER_MS);
    const onOrphanCreated: NetworkEventHandler = (rawData?: unknown) => {
        const data = rawData as RoomCreated | undefined;
        if (!data?.aiRoom || data.gameType !== 'standard' || data.requestId !== orphanId) return;
        dropOrphan();
        // The abandoned request's room: leave it (if a newer request already
        // replaced it on the server, this is a harmless no-op)
        if (gameState.roomCode !== data.roomCode) networkManager.leaveRoom(data.roomCode);
    };
    const onOrphanError: NetworkEventHandler = (rawData?: unknown) => {
        if ((rawData as ErrorMessage | undefined)?.requestId === orphanId) dropOrphan();
    };
    networkManager.on('room-created', onOrphanCreated);
    networkManager.on('create-ai-room-error', onOrphanError);
}

// Drop a persisted SP AI-room session, leaving MP sessions alone —
// localStorage is shared across tabs, so only clear what we own.
function clearStandardAIRoomSession(): void {
    const session = Reconnection.getSession();
    if (session?.aiRoom) Reconnection.clearSession();
}

// Connect failure / room-creation failure UX: SP requires the server now —
// show a clear error and return to the menu (run state, if any, stays saved).
function abortStandardServerSP(): void {
    gameState.isRoguelike = false;
    gameState.isTactical = false;
    renderer.showToast('Could not reach the server — single-player requires a connection', true);
    renderer.showScreen('menu-screen');
    BugReport.setCurrentScreen('menu');
}

async function startStandardServerSP(faction: string, aiConfig: RoguelikeAIConfig | null): Promise<void> {
    // One start at a time: covers the connect wait, before the listener exists
    if (standardAiRoomHandler || standardAiStartInFlight === standardAiRoomGeneration) return;
    const generation = standardAiRoomGeneration;
    standardAiStartInFlight = generation;
    try {
        await startStandardServerSPInner(faction, aiConfig);
    } finally {
        // An abandoned start must not release a newer one's lock
        if (standardAiStartInFlight === generation) standardAiStartInFlight = null;
    }
}

async function startStandardServerSPInner(faction: string, aiConfig: RoguelikeAIConfig | null): Promise<void> {
    if (standardAiRoomHandler) return; // room creation already in flight

    // Leaving (MAIN MENU / Back) while still connecting cancels this start:
    // cancelPendingStandardAIRoom() bumps the generation.
    const generation = standardAiRoomGeneration;
    try {
        await networkManager.connect();
    } catch (err) {
        if (generation !== standardAiRoomGeneration) return;
        console.warn('[standard-sp] Server unreachable — cannot start single-player:', err);
        abortStandardServerSP();
        return;
    }
    if (generation !== standardAiRoomGeneration) return;

    const node = roguelikeCurrentNode;
    const runState = roguelikeRunState;
    const isRoguelikeBattle = !!(aiConfig && node);

    const requestId = newAiRequestId();
    standardAiRequestId = requestId;

    const onCreated: NetworkEventHandler = (rawData?: unknown) => {
        const data = rawData as RoomCreated | undefined;
        if (!data?.aiRoom || data.gameType !== 'standard' || data.requestId !== requestId) return;
        cancelPendingStandardAIRoom();
        standardAiRequestId = null;

        gameState.roomCode = data.roomCode;
        gameState.playerId = data.playerId;
        gameState.opponentFaction = data.aiFaction;
        if (data.abilities) {
            abilityManager.updateFromServer(data.abilities);
        }
        // In-memory token: lets a silent socket blip auto-rejoin mid-battle
        // even for roguelike battles, which never persist a session.
        Reconnection.setAIRoomSessionToken(data.sessionToken || null);

        // Plain SP persists the session (with the aiRoom marker) so a page
        // refresh can resume this battle via the reconnect modal, same as MP.
        // Roguelike battles deliberately do NOT save one — run state owns
        // those — and drop any stale plain-SP marker (the server replaced
        // that socket's old AI room anyway).
        if (!isRoguelikeBattle && data.sessionToken) {
            const variant: StandardVariant | null = gameState.isBlitz ? 'blitz' : (gameState.isTimeTrial ? 'time-trial' : null);
            Reconnection.saveSession(data.roomCode, data.sessionToken, faction, {
                aiRoom: true,
                gameType: 'standard',
                opponentName: gameState.opponentName,
                ...(variant ? { variant } : {}),
            });
        } else if (isRoguelikeBattle) {
            clearStandardAIRoomSession();
        }

        // Enemy faction colors come from the server-resolved AI faction
        const factions = getFactions();
        const enemyFactionData = factions[data.aiFaction];
        if (enemyFactionData?.colors) {
            renderer.setFactionColors(null, enemyFactionData.colors);
        }

        // Remote driver: battle actions go over the wire from here on
        if (driver) driver.dispose();
        driver = new StandardRemoteDriver(networkManager, data.roomCode);

        renderer.showScreen('placement-screen');
        BugReport.setCurrentScreen('placement');
        PlacementPhase.initializePlacement();
    };

    let throttleRetries = 0;
    const onError: NetworkEventHandler = (rawData?: unknown) => {
        const data = rawData as ErrorMessage | undefined;
        // Every reply to an id-bearing request echoes its id (the handler's
        // errors and the validation / rate-limit ones), so anything else
        // answers another, abandoned request
        if (data?.requestId !== requestId) return;
        // Transient server throttle: keep the pending creation and resend once
        // the rate-limit window has passed (a few times at most)
        if (data?.retryable && throttleRetries < 3) {
            throttleRetries++;
            setTimeout(() => {
                // Still our pending creation (not cancelled / abandoned meanwhile)
                if (standardAiRoomErrorHandler === onError) networkManager.send('create-ai-room', payload);
            }, 1100);
            return;
        }
        cancelPendingStandardAIRoom();
        standardAiRequestId = null;
        console.warn('[standard-sp] AI room creation failed:', data?.message);
        abortStandardServerSP();
    };

    standardAiRoomHandler = onCreated;
    standardAiRoomErrorHandler = onError;
    networkManager.on('room-created', onCreated);
    networkManager.on('create-ai-room-error', onError);

    const playerName = (gameState.playerName || 'Commander').slice(0, 15).trim() || 'Commander';
    const payload: CreateAIRoomRequest = {
        requestId,
        playerName,
        faction,
        difficulty: aiConfig ? aiConfig.difficulty : ((playerProfile.difficulty || 'knight') as StandardDifficulty),
    };
    if (isRoguelikeBattle) {
        payload.aiFaction = node!.faction;
        payload.roguelike = buildStandardRoguelikeWire(aiConfig!, node!, runState);
    } else if (gameState.isBlitz) {
        payload.variant = 'blitz';
    } else if (gameState.isTimeTrial) {
        payload.variant = 'time-trial';
    }
    networkManager.send('create-ai-room', payload);
}

/**
 * Serialize the resolved roguelike battle config (buildAIConfig output + the
 * player's run state) into the create-ai-room `roguelike` wire shape.
 * Resolved data only — numbers and ability ids, never functions; the server
 * re-resolves ability defs from its own data and clamps numeric overrides
 * against the roguelike-config upgrade caps.
 */
function buildStandardRoguelikeWire(
    aiConfig: RoguelikeAIConfig,
    node: RoguelikeNodeView,
    runState: RoguelikeRunStateView | null
): StandardRoguelikeBattleConfig {
    const numericParams = (params: Record<string, unknown> | undefined): Record<string, number> => {
        const out: Record<string, number> = {};
        for (const [k, v] of Object.entries(params || {})) {
            if (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 999) out[k] = v;
        }
        return out;
    };
    // Ability defs ({ id, limits, params }) → wire entries ({ id, usesPerGame?, cooldownTurns?, params? })
    const toWireAbility = (a: RunAbilityDef): UpgradedAbility => {
        const entry: UpgradedAbility = { id: a.id };
        if (typeof a.limits?.usesPerGame === 'number') entry.usesPerGame = a.limits.usesPerGame;
        if (typeof a.limits?.cooldownTurns === 'number') entry.cooldownTurns = a.limits.cooldownTurns;
        const params = numericParams(a.params);
        if (Object.keys(params).length > 0) entry.params = params;
        return entry;
    };

    const wire: StandardRoguelikeBattleConfig = {
        bonusAbilityUses: aiConfig.bonusAbilityUses || 0,
        cooldownReduction: aiConfig.cooldownReduction || 0,
        extraShots: aiConfig.extraShots || 0,
        preRevealShips: aiConfig.preRevealShips || 0,
        flagshipArmor: aiConfig.flagshipArmor || 0,
        bonusAbilities: aiConfig.bonusAbilities || 0,
        pendingDamage: runState?.pendingDamage || 0,
    };

    // Modifier ids ride along for logging only — behavior comes from the
    // resolved fields above, and the banner renders from client node state.
    const modifierIds = (node.modifiers || [])
        .map(m => m.id)
        .filter((id): id is string => typeof id === 'string' && /^[a-z0-9_-]{1,40}$/.test(id));
    if (modifierIds.length > 0) wire.modifierIds = modifierIds;

    if (typeof aiConfig.abilityChanceOverride === 'number') {
        wire.abilityChanceOverride = aiConfig.abilityChanceOverride;
    }
    if (Array.isArray(aiConfig.randomAbilities) && aiConfig.randomAbilities.length > 0) {
        wire.randomAbilities = aiConfig.randomAbilities.map(toWireAbility);
    }
    if (Array.isArray(runState?.abilities) && runState.abilities.length > 0) {
        wire.upgradedAbilities = runState.abilities.map(toWireAbility);
    }

    // Ship damage carry-over (mirrors the local startBattle application; the
    // server applies it and reports it back via game-start.roguelike)
    const rlConfig = getRoguelike().getConfig();
    if ((rlConfig.general?.damageCarryOver ?? true) && runState?.shipDamage
        && Object.keys(runState.shipDamage).length > 0) {
        wire.shipDamage = runState.shipDamage;
    }

    return wire;
}

// Ship placement handlers
function setupPlacementHandlers(): void {
    renderer.setupPlacementBoard(
        PlacementPhase.handleShipDrop,
        PlacementPhase.handlePlacementClick,
        PlacementPhase.handlePlacementRightClick,
        (ship, x, y) => gameState.playerBoard.canPlaceShip(ship, x, y)
    );

    document.getElementById('btn-random-placement')!.addEventListener('click', () => {
        if (PlacementPhase.isPlacementLocked()) return;
        gameState.playerBoard.randomPlacement(gameState.ships);
        renderer.renderPlacementBoard(gameState.playerBoard);
        renderer.updateShipList(gameState.ships);
        renderer.updateReadyButton(gameState.allShipsPlaced());
    });

    document.getElementById('btn-clear-placement')!.addEventListener('click', () => {
        if (PlacementPhase.isPlacementLocked()) return;
        gameState.playerBoard.clear();
        renderer.renderPlacementBoard(gameState.playerBoard);
        renderer.updateShipList(gameState.ships);
        renderer.updateReadyButton(false);
    });

    document.getElementById('btn-ready')!.addEventListener('click', () => {
        if (!gameState.allShipsPlaced() || PlacementPhase.isPlacementLocked()) return;

        // MP and server-hosted SP (AI rooms) alike: submit ships and wait
        // for the server's game-start. The layout is frozen from here on —
        // the server keeps exactly what was submitted — until UNREADY.
        const ships = gameState.playerBoard.getShipsJSON();
        networkManager.shipsPlaced(gameState.roomCode!, ships);
        PlacementPhase.markReady(ships);
    });

    // Take READY back (multiplayer, before the battle starts). Placement
    // unlocks when the server confirms (ships-unready-ok); if the opponent
    // readied first the game starts instead (game-start).
    document.getElementById('btn-unready')!.addEventListener('click', () => {
        if (!PlacementPhase.isPlacementLocked() || !gameState.roomCode) return;
        const btn = document.getElementById('btn-unready') as HTMLButtonElement;
        btn.disabled = true;
        btn.textContent = 'UNREADYING...';
        networkManager.shipsUnready(gameState.roomCode);
    });

    document.getElementById('btn-back-placement')!.addEventListener('click', () => {
        if (gameState.mode === 'multiplayer') {
            // Leave the room and go back to lobby
            if (gameState.roomCode) {
                networkManager.leaveRoom(gameState.roomCode);
            }
            Reconnection.clearSession();
            gameState.reset();
            abilityManager.reset();
            abilityRenderer.reset();
            renderer.showScreen('lobby-screen');
            BugReport.setCurrentScreen('lobby');
        } else {
            // Singleplayer: abandon the server-hosted AI room if one exists
            // (the server replaces it on the next create) and drop its resume
            // session so the abandoned room is never offered for rejoin.
            if (driver) {
                if (gameState.roomCode) {
                    networkManager.leaveRoom(gameState.roomCode);
                }
                driver.dispose();
                driver = null;
                gameState.roomCode = null;
                Reconnection.setAIRoomSessionToken(null);
                clearStandardAIRoomSession();
            }
            // Go back to faction selection
            gameState.playerBoard = new Board();
            gameState.ships = [];
            renderer.showScreen('faction-screen');
            BugReport.setCurrentScreen('faction');
        }
    });
}

// Placement functions moved to placementPhase.js

// Battle phase handlers
function setupBattleHandlers(): void {
    renderer.setupEnemyBoard(BattlePhase.handleFire);

    // Set up player's board for self-targeting abilities (repair, decoy)
    const yourBoard = document.getElementById('your-board');
    if (yourBoard) {
        yourBoard.addEventListener('click', (e) => {
            const cell = (e.target as Element).closest<HTMLElement>('.cell');
            if (!cell) return;

            // Only handle clicks when targeting with a self-targeting ability
            if (!abilityManager.isTargeting()) return;

            const ability = abilityManager.getTargetingAbility();
            if (!ability) return;

            // Check if this ability targets own board
            const selfTargetingAbilities = ['repair', 'decoy'];
            if (!selfTargetingAbilities.includes(ability.id)) return;

            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);

            // Multi-cell repair: collect targets before firing
            if (ability.id === 'repair' && repairMaxCells > 1) {
                handleRepairCellClick(x, y, cell);
                return;
            }

            handleUseAbility(x, y);
        });

        // Right-click to rotate decoy during targeting
        yourBoard.addEventListener('contextmenu', (e) => {
            const ability = abilityManager.getTargetingAbility();
            if (!ability || ability.id !== 'decoy') return;

            e.preventDefault();
            const decoySize = (ability.params?.decoySize as number | undefined) || 1;
            if (decoySize <= 1) return; // No rotation needed for 1-cell decoys

            const horizontal = abilityManager.toggleDecoyOrientation();
            renderer.showToast(horizontal ? 'Horizontal' : 'Vertical');
            updateDecoyPreview();
        });

        // Hover preview for multi-cell decoys
        yourBoard.addEventListener('mousemove', (e) => {
            const cell = (e.target as Element).closest<HTMLElement>('.cell');
            if (!cell) {
                clearDecoyPreview();
                return;
            }

            const ability = abilityManager.getTargetingAbility();
            if (!ability || ability.id !== 'decoy') {
                clearDecoyPreview();
                return;
            }

            const decoySize = (ability.params?.decoySize as number | undefined) || 1;
            if (decoySize <= 1) return;

            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);
            showDecoyPreview(x, y, decoySize, abilityManager.getDecoyHorizontal());
        });

        yourBoard.addEventListener('mouseleave', clearDecoyPreview);
    }

    // Keyboard handler for rotating decoy (R key)
    document.addEventListener('keydown', (e) => {
        if (e.key.toLowerCase() !== 'r') return;

        const ability = abilityManager.getTargetingAbility();
        if (!ability || ability.id !== 'decoy') return;

        const decoySize = (ability.params?.decoySize as number | undefined) || 1;
        if (decoySize <= 1) return;

        const horizontal = abilityManager.toggleDecoyOrientation();
        renderer.showToast(horizontal ? 'Horizontal' : 'Vertical');
        updateDecoyPreview();
    });
}

// Show preview of where multi-cell decoy will be placed
function showDecoyPreview(x: number, y: number, size: number, horizontal: boolean): void {
    clearDecoyPreview();
    const yourBoard = document.getElementById('your-board');
    if (!yourBoard) return;

    for (let i = 0; i < size; i++) {
        const cellX = horizontal ? x + i : x;
        const cellY = horizontal ? y : y + i;

        if (cellX > 14 || cellY > 14) continue;

        const cell = yourBoard.querySelector(`[data-x="${cellX}"][data-y="${cellY}"]`);
        if (cell) {
            cell.classList.add('decoy-preview');
        }
    }
}

// Clear decoy placement preview
function clearDecoyPreview(): void {
    const previews = document.querySelectorAll('.decoy-preview');
    previews.forEach(el => el.classList.remove('decoy-preview'));
}

// Update decoy preview after rotation
function updateDecoyPreview(): void {
    const yourBoard = document.getElementById('your-board');
    if (!yourBoard) return;

    const ability = abilityManager.getTargetingAbility();
    if (!ability || ability.id !== 'decoy') return;

    // Find current hover position from existing preview
    const existingPreview = yourBoard.querySelector<HTMLElement>('.decoy-preview');
    if (existingPreview) {
        const x = parseInt(existingPreview.dataset.x!);
        const y = parseInt(existingPreview.dataset.y!);
        const decoySize = (ability.params?.decoySize as number | undefined) || 1;
        showDecoyPreview(x, y, decoySize, abilityManager.getDecoyHorizontal());
    }
}

// Handle ability click from the ability panel
function handleAbilityClick(abilityId: string, immediate = false): void {
    if (!driver) return;
    if (immediate) {
        // Ability doesn't need targeting - activate immediately
        driver.useAbility(abilityId, {});
        abilityRenderer.renderAbilities(gameState.isMyTurn);
        return;
    }

    // Show targeting mode for abilities that need a target
    const ability = abilityManager.getTargetingAbility();
    if (ability) {
        // Initialize multi-cell repair state
        if (ability.id === 'repair') {
            repairTargets = [];
            repairMaxCells = (ability.params?.cellsPerUse as number | undefined) || 1;
        }
        abilityRenderer.showTargetingMode(ability, ability.id === 'repair' ? repairMaxCells : 1);
    }
}

// Handle a cell click during multi-cell repair targeting
function handleRepairCellClick(x: number, y: number, cellEl: HTMLElement): void {
    const key = `${x},${y}`;

    // Check if already selected — toggle off
    const existingIdx = repairTargets.findIndex(t => t.x === x && t.y === y);
    if (existingIdx !== -1) {
        repairTargets.splice(existingIdx, 1);
        cellEl.classList.remove('repair-selected');
        updateRepairTargetingUI();
        return;
    }

    // Validate: must be a hit cell on a non-sunk ship
    const shipId = gameState.playerBoard.grid[y]?.[x];
    if (!shipId) {
        renderer.showToast('No ship at this cell.', true);
        return;
    }
    if (!gameState.playerBoard.shots.has(key)) {
        renderer.showToast('This cell is not damaged.', true);
        return;
    }
    const ship = gameState.ships.find(s => s.id === shipId);
    if (ship && ship.isSunk()) {
        renderer.showToast('Cannot repair a destroyed ship.', true);
        return;
    }

    // Already at max?
    if (repairTargets.length >= repairMaxCells) {
        renderer.showToast(`Already selected ${repairMaxCells} cells.`, true);
        return;
    }

    // Add to selection
    repairTargets.push({ x, y });
    cellEl.classList.add('repair-selected');
    updateRepairTargetingUI();

    // Auto-confirm when max cells selected
    if (repairTargets.length >= repairMaxCells) {
        setTimeout(() => confirmRepairTargets(), 200);
    }
}

// Update the targeting indicator to show selection count
function updateRepairTargetingUI(): void {
    const counter = document.getElementById('repair-target-counter');
    if (counter) {
        counter.textContent = `${repairTargets.length} / ${repairMaxCells} cells selected`;
    }
    // Enable/disable confirm button
    const confirmBtn = document.getElementById('repair-confirm-btn') as HTMLButtonElement | null;
    if (confirmBtn) {
        confirmBtn.disabled = repairTargets.length === 0;
    }
}

// Fire the multi-cell repair with collected targets
function confirmRepairTargets(): void {
    if (repairTargets.length === 0 || !driver) return;

    const targets = [...repairTargets];
    clearRepairSelection();

    driver.useAbility('repair', { cells: targets });

    abilityManager.cancelTargeting();
    abilityRenderer.hideTargetingMode();
    abilityRenderer.renderAbilities(gameState.isMyTurn);
}

// Clear repair selection visual markers
function clearRepairSelection(): void {
    const yourBoard = document.getElementById('your-board');
    if (yourBoard) {
        yourBoard.querySelectorAll('.repair-selected').forEach(el => {
            el.classList.remove('repair-selected');
        });
    }
    repairTargets = [];
}

// Expose for abilityRenderer cross-module access
window.confirmRepairTargets = confirmRepairTargets;
window.clearRepairSelection = clearRepairSelection;

// Handle using an ability (after targeting)
async function handleUseAbility(x: number, y: number): Promise<boolean> {
    const ability = abilityManager.getTargetingAbility();
    if (!ability || !driver) return false;

    const abilityId = ability.id;

    // Special handling for sector_sweep - use pre-selected axis
    if (abilityId === 'sector_sweep') {
        const axis = abilityRenderer.sectorSweepAxis;
        const index = axis === 'row' ? y : x;

        driver.useAbility('sector_sweep', { axis, index });

        // Cancel targeting mode
        abilityManager.cancelTargeting();
        abilityRenderer.hideTargetingMode();
        abilityRenderer.renderAbilities(gameState.isMyTurn);
        return true;
    }

    // Special handling for orbital_bombardment - use pre-selected axis (same UX as sector_sweep)
    if (abilityId === 'orbital_bombardment') {
        const axis = abilityRenderer.sectorSweepAxis;
        const index = axis === 'row' ? y : x;

        driver.useAbility('orbital_bombardment', { axis, index });

        abilityManager.cancelTargeting();
        abilityRenderer.hideTargetingMode();
        abilityRenderer.renderAbilities(gameState.isMyTurn);
        return true;
    }

    // Build target with orientation for decoy
    const target: AbilityTargetPayload = { x, y };
    if (abilityId === 'decoy') {
        target.horizontal = abilityManager.getDecoyHorizontal();
    }

    driver.useAbility(abilityId, target);

    // Cancel targeting mode
    abilityManager.cancelTargeting();
    abilityRenderer.hideTargetingMode();
    abilityRenderer.renderAbilities(gameState.isMyTurn);

    return true;
}

// Battle functions (startBattle, handleFire, endGame, handleAbilityResult,
// logSinglePlayerMatch, checkTimeTrialPersonalBest,
// renderRoguelikeModifierBanner) moved to battlePhase.js

// Tactical game-over handlers
function setupTacticalGameOverHandlers(): void {
    document.getElementById('btn-tactical-main-menu')?.addEventListener('click', () => {
        // Leave tactical MP room if active
        if (gameState.isTactical && gameState.mode === 'multiplayer' && gameState.roomCode) {
            networkManager.leaveRoom(gameState.roomCode);
        }
        if (getTactical()) getTactical()!.cleanupTactical();
        gameState.reset();
        // reset() keeps flags set by other modes (tactical campaign, strategy,
        // tournament); leaving to the menu must clear them too
        NetworkHandlers.clearExternalModeFlags();
        const factions = getFactions();
        const firstFaction = Object.values(factions)[0];
        renderer.setFactionStyle('default', firstFaction?.colors || { primary: '#ff6b35', secondary: '#f7931e' });
        renderer.showScreen('menu-screen');
        BugReport.setCurrentScreen('menu');
    });

    // The ONLY binding for Play Again (tactical/main.ts deliberately does not
    // bind it). Shown only on the plain-SP game-over screen: MP/tournament
    // hide it and campaign battles never reach that screen. Replays with the
    // same faction straight to the fleet builder; falls back to the faction
    // screen if there is nothing to replay.
    document.getElementById('btn-tactical-play-again')?.addEventListener('click', () => {
        const tactical = getTactical();
        if (gameState.mode !== 'singleplayer') {
            if (tactical) tactical.cleanupTactical();
            return;
        }
        gameState.reset();
        gameState.mode = 'singleplayer';
        gameState.isTactical = true;
        gameState.playerName = playerProfile.displayName;
        if (tactical && tactical.playAgain(renderer)) return;
        showDifficultySelector(false);
        renderer.showScreen('faction-screen');
        BugReport.setCurrentScreen('faction');
    });
}

// Game over handlers
function setupGameOverHandlers(): void {
    document.getElementById('btn-rematch')!.addEventListener('click', () => {
        if (gameState.mode === 'singleplayer') {
            // Reset for new game (preserve mode flags)
            const wasBlitz = gameState.isBlitz;
            const wasTimeTrial = gameState.isTimeTrial;
            const wasTactical = gameState.isTactical;
            const wasFaction = gameState.faction;
            gameState.reset();
            abilityManager.reset();
            abilityRenderer.reset();
            renderer.clearBattleBoards();
            gameState.mode = 'singleplayer';
            gameState.isBlitz = wasBlitz;
            gameState.isTimeTrial = wasTimeTrial;
            gameState.isTactical = wasTactical;
            if (!wasTactical && wasFaction) {
                // True rematch: same faction, variant (blitz / time trial) and
                // difficulty (the saved profile difficulty) — straight to
                // placement in a fresh AI room (the server replaces the
                // finished one). The difficulty selector mirrors the kept
                // variant should the player later come back to the faction screen.
                showDifficultySelector(true);
                // No double-submits while the room is created (the next
                // game over re-enables it); MAIN MENU stays usable and
                // abandons the pending room.
                (document.getElementById('btn-rematch') as HTMLButtonElement).disabled = true;
                void selectFaction(wasFaction);
                return;
            }
            showDifficultySelector(!wasTactical);
            renderer.showScreen('faction-screen');
            BugReport.setCurrentScreen('faction');
        } else {
            // Request rematch in multiplayer
            networkManager.requestRematch(gameState.roomCode!);
            (document.getElementById('btn-rematch') as HTMLButtonElement).disabled = true;
        }
    });

    document.getElementById('btn-main-menu')!.addEventListener('click', () => {
        // A direct SP REMATCH may still be creating its AI room: a late
        // room-created must not send the player back to placement.
        abandonPendingStandardAIRoom();
        // Leave MP rooms and server-hosted SP AI rooms alike (the AI room is
        // finished at this point; leaving frees it server-side immediately)
        if (gameState.roomCode
            && (gameState.mode === 'multiplayer' || driver)) {
            networkManager.leaveRoom(gameState.roomCode);
        }
        // Preserve roguelike run state — player can resume from the menu
        if (gameState.isRoguelike && roguelikeRunState) {
            roguelikeCurrentNode = null;
            getRoguelike().saveActiveRun(roguelikeRunState);
        }
        gameState.reset();
        NetworkHandlers.clearExternalModeFlags();
        abilityManager.reset();
        abilityRenderer.reset();
        renderer.clearBattleBoards();
        // Reset to default colors
        const factions = getFactions();
        const firstFaction = Object.values(factions)[0];
        renderer.setFactionStyle('default', firstFaction?.colors || { primary: '#ff6b35', secondary: '#f7931e' });
        renderer.showScreen('menu-screen');
        BugReport.setCurrentScreen('menu');
        getRoguelikeUI().updateRoguelikeResumeBanner();
    });

    // New opponent button (quickplay only)
    document.getElementById('btn-new-opponent')!.addEventListener('click', async () => {
        // Save info before reset
        const roomCode = gameState.roomCode;
        const faction = gameState.faction!;
        const playerName = gameState.playerName;
        const wasTactical = gameState.isTactical;
        const factions = getFactions();
        const factionData = factions[faction];
        const factionAbilities = factionData?.abilities || [];

        // Reset game state first to prevent processing stale events
        gameState.reset();
        abilityRenderer.reset();
        renderer.clearBattleBoards();

        // Then leave room (server will clean up)
        if (roomCode) {
            networkManager.leaveRoom(roomCode);
        }
        gameState.mode = 'multiplayer';
        gameState.playerName = playerName;
        gameState.isTactical = wasTactical;
        gameState.initializeShips(faction);

        // Re-apply faction styling
        renderer.setFactionStyle(faction, factionData.colors);
        ThemeManager.setFaction(faction);

        // Re-initialize abilities
        if (factionData.abilityData) {
            abilityManager.initialize(factionData.abilityData);
        }

        // Join quickplay queue
        try {
            await networkManager.connect();
            const session = Reconnection.getSession();
            const gameType = wasTactical ? 'tactical' : 'standard';
            networkManager.joinQuickplay(playerName, faction, factionAbilities, session?.sessionToken, gameType);
            renderer.showScreen('queue-screen');
        } catch {
            renderer.showToast('Failed to connect to server', true);
            renderer.showScreen('menu-screen');
        }
    });
}

// ==========================
// Space Fleet Strategy UI
// ==========================

function setupStrategyHandlers(): void {
    // New strategy campaign
    document.getElementById('btn-strategy')?.addEventListener('click', () => {
        if (getStrategy().hasSavedGame()) {
            // If saved game exists, prompt to resume
            const banner = document.getElementById('strategy-resume-banner');
            if (banner) {
                banner.classList.remove('hidden');
                banner.classList.add('highlight');
                setTimeout(() => banner.classList.remove('highlight'), 1500);
            }
            return;
        }
        gameState.mode = 'singleplayer';
        gameState.isBlitz = false;
        gameState.isTimeTrial = false;
        showDifficultySelector(true);
        // Hide blitz/time-trial toggles (not applicable to strategy)
        document.getElementById('blitz-toggle')?.classList.add('hidden');
        document.getElementById('time-trial-toggle')?.classList.add('hidden');
        // Set a flag so faction selection knows to launch strategy mode
        gameState.isStrategy = true;
        renderer.showScreen('faction-screen');
    });

    // Resume saved strategy campaign
    document.getElementById('btn-strategy-resume')?.addEventListener('click', () => {
        getStrategy().resumeStrategy({ renderer });
    });

    // Abandon strategy campaign
    document.getElementById('btn-strategy-abandon-menu')?.addEventListener('click', () => {
        if (!confirm('Abandon your campaign? Progress will be lost.')) return;
        getStrategy().cleanupStrategy();
        getStrategy().clearSave();
        const banner = document.getElementById('strategy-resume-banner');
        if (banner) banner.classList.add('hidden');
    });
}

// Roguelike campaign UI functions moved to roguelikeUI.ts
// Reconnection functions (checkAndOfferReconnection, showReconnectionOffer, attemptRejoin,
// showReconnectionModal, hideReconnectionModal, showOpponentDisconnectOverlay,
// hideOpponentDisconnectOverlay, restorePlacementState, restoreBattleState,
// setupReconnectionHandlers) moved to reconnection.js

// Network event handlers (setupNetworkHandlers, updateQueueDisplay) moved to networkHandlers.js

// Clean up timers on page unload to prevent leaks
window.addEventListener('beforeunload', () => {
    BattlePhase.stopGameTimer();
    const reconnectInterval = Reconnection.getReconnectCountdownInterval();
    if (reconnectInterval) {
        clearInterval(reconnectInterval);
    }
});

// Start the app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    init().catch((err: unknown) => {
        console.error('Startup failed:', err);
        // Replace the loading indicator only if the menu never unlocked; a
        // failure in the post-ready session/tournament flow keeps the menu.
        if (document.body.dataset.ready !== '1') showStartupError();
    });
});
